import { Ticker } from '@/ts/fetchMarketCarpetData';
import { format } from 'd3';
import { PreviousCurrentPairIterator } from '@/ts/util/iterators';
import { findMax, findMin, getMovingAverage, isInteger } from '@/ts/util/util';

export interface CalculatedValues {
  value: number;
  display: string;
  displayCondensed: string;
}

// Abstract class definitions
export abstract class Calculator {
  abstract calculate(ticker: Ticker, numDays: number): number;
  abstract convert(value: number, numDays: number): string;
  abstract convertCondensed(value: number, numDays: number): string; // TODO: why do we need numDays here? Fullstoch needs it for display, but we should be able to get around that

  getCalculations(ticker: Ticker, numDays: number): CalculatedValues {
    const computedValue = this.calculate(ticker, numDays);
    // Guard against NaN/Infinity from degenerate data (e.g. division by a
    // zero-price that was coerced from a null scanengine servlet V3 carpet
    // API closePrice).
    if (!Number.isFinite(computedValue))
      return { value: NaN, display: 'N/A', displayCondensed: 'N/A' };
    return {
      value: computedValue,
      display: this.convert(computedValue, numDays),
      displayCondensed: this.convertCondensed(computedValue, numDays)
    };
  }
}

export abstract class PercentageCalculator extends Calculator {
  convert(value: number): string {
    return format("+.2%")(value);
  }

  convertCondensed(value: number): string {
    return format('+~%')(value);
  }
}

export abstract class DecimalCalculator extends Calculator {
  convert(value: number): string {
    return format("+.2f")(value);
  }

  convertCondensed(value: number): string {
    return format('+~')(value);
  }
}

export abstract class SignOnlyOnDeltaPercentageCalculator extends Calculator { // we don't care about explicitly having the sign if delta mode is on
  convert(value: number, numDays: number): string {
    return numDays === 1 ? format(".2%")(value) : format("+.2%")(value);
  }

  convertCondensed(value: number, numDays: number): string {
    return numDays === 1 ? format('~%')(value) : format('+~%')(value);
  }
}

export abstract class SignOnlyOnDeltaDecimalCalculator extends Calculator { // we don't care about explicitly having the sign if delta mode is on
  convert(value: number, numDays: number): string {
    return numDays === 1 ? format(".2f")(value) : format("+.2f")(value);
  }

  convertCondensed(value: number, numDays: number): string {
    return numDays === 1 ? format('~f')(value) : format('+~f')(value);
  }
}

export class PercentagePerformanceCalculator extends PercentageCalculator {
  calculate(ticker: Ticker, numDays: number): number {
    const { prices } = ticker;
    const startIndex = numDays - 1;
    const endIndex = 0;

    return ( (prices[endIndex] - prices[startIndex]) / prices[startIndex]);
  }
}

export class StockChartsTechnicalRankCalculator extends SignOnlyOnDeltaDecimalCalculator {
  calculate(ticker: Ticker, numDays: number): number {
    const { sctr } = ticker;
    const startValue = sctr[0];
    const endValue = sctr[numDays - 1];

    if (startValue === null || endValue === null)
      return NaN;

    if (numDays === 1)
      return startValue;
    return (startValue - endValue);
  }

  override getCalculations(ticker: Ticker, numDays: number): CalculatedValues {
    const value = this.calculate(ticker, numDays);
    if (Number.isNaN(value))
      return { value: NaN, display: 'N/A', displayCondensed: 'N/A' };
    return {
      value,
      display: this.convert(value, numDays),
      displayCondensed: this.convertCondensed(value, numDays)
    };
  }
}

export class UpDaysDownDaysCalculator extends Calculator {
  calculate(ticker: Ticker, numDays: number): number {
    const { prices } = ticker;
    const endIndex = numDays;

    const priceInterval = prices.slice(0, endIndex);

    const upDownAmount = priceInterval.reduce((upDownCounter, current, i) => {
      if (i === 0)
        return upDownCounter;

      const previous = priceInterval[i - 1];

      if (previous === current)
        return upDownCounter;

      const increment = previous > current ? 1 : -1;
      return upDownCounter + increment;
    }, 0);

    return upDownAmount;
  }

  convert(value: number): string {
    if (value === 0)
      return 'Neutral';

    return `${value > 0 ? 'Up' : 'Down'} ${Math.round(Math.abs(value))}`
  }

  convertCondensed(value: number): string {
    if (isInteger(value, 0.0001))
      return `${value > 0 ? '+' : '-'}${Math.round(Math.abs(value))}`;
    return '';
  }
}

export class RelativeStrengthIndexCalculator extends SignOnlyOnDeltaDecimalCalculator {
  calculate(ticker: Ticker, numDays: number): number {
    const { prices, startingMovingAverages } = ticker;

    const prevGain = startingMovingAverages.rsiFourteenDayEmaGain;
    const prevLoss = startingMovingAverages.rsiFourteenDayEmaLoss;

    const PERIOD = 14;
    const startIndex = 0;
    const endIndex = numDays - 1;

    // Need at least x days of data
    if (endIndex > prices.length - PERIOD)
      return 0;

    const getRelativeStrengthAtIndex = (index: number) => {
      const pricesInterval = prices.slice(index).toReversed();

      const gainsLosses = getGainsAndLosses(pricesInterval);
      const lastMovingAverages = findLastMovingAveragePair(gainsLosses, PERIOD, { gainAverage: prevGain, lossAverage: prevLoss });
      const newRelativeStrength = lastMovingAverages.gainAverage / lastMovingAverages.lossAverage;

      return 100 - (100 / (1 + newRelativeStrength));
    }

    const differenceValue = numDays > 1 ? getRelativeStrengthAtIndex(endIndex) : 0;
    return getRelativeStrengthAtIndex(startIndex) - differenceValue;
  }
}

export class PercentagePriceOscillatorCalculator extends PercentageCalculator {
  calculate(ticker: Ticker, numDays: number): number {
    const { prices, startingMovingAverages } = ticker;

    const endIndex = numDays;
    const startIndex = 0;

    const FAST_PERIOD = 12;
    const SLOW_PERIOD = 26;

    const getPercentagePriceAtIndex = (index: number) => {
      const priceIntervalReversed = prices.slice(index !== 0 ? index - 1 : 0, prices.length - 1).toReversed();
      const fastEMA = findLastMovingAveragePercentage(priceIntervalReversed, FAST_PERIOD, startingMovingAverages.ppoTwelveDayEma);
      const slowEMA = findLastMovingAveragePercentage(priceIntervalReversed, SLOW_PERIOD, startingMovingAverages.ppoTwentySixDayEma);

      return (fastEMA - slowEMA) / slowEMA;
    }

    const differenceValue = numDays > 1 ? getPercentagePriceAtIndex(endIndex) : 0;
    return getPercentagePriceAtIndex(startIndex) - differenceValue;
  }
}

export class BollingerBandWidthCalculator extends SignOnlyOnDeltaDecimalCalculator {
  calculate(ticker: Ticker, numDays: number): number {
    const { prices } = ticker;

    const startIndex = 0;
    const endIndex = numDays - 1;
    const PERIOD = 20;

    // Need at least x days of data
    if (endIndex > prices.length - PERIOD)
      return 0;

    const getBollingerBandWidthAtIndex = (index: number) => {
      const simpleMovingAverages = getMovingAverage(prices.slice(index), PERIOD);
      const midValue = simpleMovingAverages[0];

      const elementRangeFromIndexDaysAgo = prices.slice(0 + index, PERIOD + index);
      const sumOfDeviationSquared = elementRangeFromIndexDaysAgo.reduce((sum, value) => sum + Math.pow(value - midValue, 2), 0);
      const standardDeviation = Math.sqrt(sumOfDeviationSquared / elementRangeFromIndexDaysAgo.length);

      const upperRangeLimit = midValue + (2 * standardDeviation);
      const lowerRangeLimit = midValue - (2 * standardDeviation);

      return 100 * ((upperRangeLimit - lowerRangeLimit) / midValue);
    }

    const differenceValue = numDays > 1 ? getBollingerBandWidthAtIndex(endIndex) : 0;
    return getBollingerBandWidthAtIndex(startIndex) - differenceValue;
  }
}

export class BollingerBandPositionCalculator extends DecimalCalculator {
  calculate(ticker: Ticker, numDays: number): number {
    const { prices } = ticker;
    const startIndex = 0;
    const endIndex = numDays - 1;
    const PERIOD = 20;

    // Need at least x days of data
    if (endIndex > prices.length - PERIOD)
      return 0;

    const getBollingerPositionAtIndex = (index: number) => {
      const simpleMovingAverages = getMovingAverage(prices.slice(index), PERIOD);
      const midValue = simpleMovingAverages[0];

      const elementRangeFromIndexDaysAgo = prices.slice(0 + index, PERIOD + index);
      const sumOfDeviationSquared = elementRangeFromIndexDaysAgo.reduce((sum, value) => sum + Math.pow(value - midValue, 2), 0);
      const standardDeviation = Math.sqrt(sumOfDeviationSquared / elementRangeFromIndexDaysAgo.length);

      const upperRangeLimit = midValue + (2 * standardDeviation);
      const lowerRangeLimit = midValue - (2 * standardDeviation);

      let stockVal = prices[index];

      const multiplier = stockVal > midValue ? 100 : -100;
      const rangeValue = stockVal > midValue ? upperRangeLimit : lowerRangeLimit

      return multiplier * (stockVal - midValue) / (rangeValue - midValue);
    }

    const differenceValue = numDays > 1 ? getBollingerPositionAtIndex(endIndex) : 0;
    return getBollingerPositionAtIndex(startIndex) - differenceValue;
  }
}

interface GainLossMovingAverage {
    gainAverage: number;
    lossAverage: number;
}

export class StochasticRelativeStrengthIndexCalculator extends SignOnlyOnDeltaDecimalCalculator {
  calculate(ticker: Ticker, numDays: number): number {
    const { prices, startingMovingAverages } = ticker;
    const startIndex = 0;
    const endIndex = numDays - 1;

    // Need at least x days of data
    const PERIOD = 14;
    if (endIndex > prices.length - PERIOD)
      return 0;

    const getStockRsiAtIndex = (index: number) => {
      const pricesInterval = prices.slice(index).toReversed();

      const prevGain = startingMovingAverages.rsiFourteenDayEmaGain;
      const prevLoss = startingMovingAverages.rsiFourteenDayEmaLoss;


      const gainsLosses = getGainsAndLosses(pricesInterval);
      const gainLossMovingAverages = findGainLossMovingAverage(gainsLosses, PERIOD, {
        gainAverage: prevGain,
        lossAverage: prevLoss
      });

      const relativeStrengthIndexNew = gainLossMovingAverages.map(({ gainAverage, lossAverage }) => (100 * gainAverage) / (gainAverage + lossAverage));

      let stochRSI = relativeStrengthIndexNew[PERIOD];
      for (let i = 1; i < relativeStrengthIndexNew.length; i++) {
        const sliceIndexes: [number, number] = i < PERIOD ? [1, i + 1] : [i - PERIOD + 1, i + 1];
        const sliced = relativeStrengthIndexNew.slice(...sliceIndexes);
        const highest = Math.max(...sliced);
        const lowest = Math.min(...sliced);

        stochRSI = (highest !== lowest) ? (relativeStrengthIndexNew[i] - lowest) / (highest - lowest) : relativeStrengthIndexNew[i];
      }
      return stochRSI;
    }

    const differenceValue = numDays > 1 ? getStockRsiAtIndex(endIndex) : 0;
    return numDays === 1 ? getStockRsiAtIndex(startIndex) : getStockRsiAtIndex(startIndex) - differenceValue;
  }
}

export class AroonOscillatorCalculator extends DecimalCalculator {
  calculate(ticker: Ticker, numDays: number): number {
    const { prices } = ticker;
    const startIndex = 0;
    const endIndex = numDays - 1;
    const PERIOD = 25;

    const getAroonOscillatorAtIndex = (index: number) => {
      // Need at least x days of data
      if (index > prices.length - PERIOD)
        return 0;

      const periodStartIndex = index + PERIOD;
      const max = findMax(prices, index, periodStartIndex);
      const min = findMin(prices, index, periodStartIndex);

      return (100 / PERIOD) * (min.index - max.index);
    }

    const differenceValue = numDays > 1 ? getAroonOscillatorAtIndex(endIndex) : 0;
    return getAroonOscillatorAtIndex(startIndex) - differenceValue;
  }
}

export class FullStochasticsCalculator extends SignOnlyOnDeltaPercentageCalculator {
  calculate(ticker: Ticker, numDays: number): number {
    const { prices } = ticker;
    const startIndex = 0;
    const endIndex = numDays - 1;
    const PERIOD = 14;

    // Need at least x days of data
    if (endIndex > prices.length - PERIOD)
      return 0;

    const getFullStochasticsAtIndex = (index: number) => {
      const MIDDLE_VALUE = .5;
      const percentK = [MIDDLE_VALUE, ...new Array<number>(PERIOD - 1).fill(0)];

      const REVERSED_PRICES = prices.toReversed(); // TODO: find a way to work with prices, rather than making a new reversed array them
      let i = PERIOD;
      for (const interval of getIntervals(REVERSED_PRICES.slice(1, prices.length - index), PERIOD)) {
        const highest = Math.max(...interval);
        const lowest = Math.min(...interval);

        percentK[i] = (highest === lowest ? MIDDLE_VALUE : ((REVERSED_PRICES[i] - lowest) / (highest - lowest)));
        i++;
      }

      const simpleMovingAverage = getStochasticsMovingAverage(percentK, 3);
      return simpleMovingAverage[prices.length - 1 - index];
    }

    const differenceValue = numDays > 1 ? getFullStochasticsAtIndex(endIndex) : 0;
    return getFullStochasticsAtIndex(startIndex) - differenceValue;
  }
}

function* getIntervals<T>(values: T[], intervalLength: number) {
  const end = values.length - intervalLength;
  const currentArray = values.slice(0, intervalLength);
  for (let i = 0; i <= end;) {
    yield currentArray;
    i++;
    currentArray.shift();
    currentArray.push(values[i + intervalLength - 1]);
  }
}

interface GainLoss {
  value: number;
  type: 'gain' | 'loss';
}

function getStochasticsMovingAverage(data: number[], period: number) {
  const outData = [data[0]];

  for (let i = 1; i < data.length; i++) {
    if (i < period)
      outData[i] = ((i * outData[i - 1] + data[i]) / (i + 1));
    else
      outData[i] = (outData[i - 1] + (data[i] - data[i - period]) / period);
  }

  return outData;
}

function getGainsAndLosses(prices: number[]): GainLoss[] {
  const pairs = Array.from(PreviousCurrentPairIterator.from(prices));

  return pairs.map(pair => {
    const change = pair[1] - pair[0];
    const type = change > 0 ? 'gain' : 'loss';
    return { value: Math.abs(change), type: type };
  });
}

function findLastMovingAverage(data: number[], period: number, prevAvg: number) {
  const smoothingFactor = 1 / period;
  return data.reduce((prevVal, val) => prevVal + smoothingFactor * (val - prevVal), prevAvg);
}

function findLastMovingAveragePair(data: GainLoss[], period: number, prevAvg: GainLossMovingAverage): GainLossMovingAverage {
  const gains = data.map(obj => obj.type === 'gain' ? obj.value : 0);
  const losses = data.map(obj => obj.type === 'loss' ? obj.value : 0);

  return {
    gainAverage: findLastMovingAverage(gains, period, prevAvg.gainAverage),
    lossAverage: findLastMovingAverage(losses, period, prevAvg.lossAverage)
  }
}

function findLastMovingAveragePercentage(data: number[], period: number, prevAvg: number) {
  const smoothingFactor = 2 / (period + 1);
  return data.reduce((prev, curr) => prev + smoothingFactor * (curr - prev), prevAvg);
}

function findGainLossMovingAverage(data: GainLoss[], period: number, prevAvg: GainLossMovingAverage): GainLossMovingAverage[] {
  const gainLossMovingAverages: GainLossMovingAverage[] = [prevAvg];
  const smoothingFactor = 1 / period;

  for (let i = 1; i < data.length; i++) {
    const element = data[i];
    const { gainAverage, lossAverage } = gainLossMovingAverages[i - 1];

    gainLossMovingAverages.push({
      gainAverage: gainAverage + smoothingFactor * ((element.type === 'gain' ? element.value : 0) - gainAverage),
      lossAverage: lossAverage + smoothingFactor * ((element.type === 'loss' ? element.value : 0) - lossAverage)
    });
  }
  return gainLossMovingAverages;
}